import {
  addDays,
  adherenceRate,
  defaultSecondaryTargets,
  deviceTimeZone,
  impliedEnergyBalance,
  localDateString,
  loggingStreak,
  type MealSlot,
  NUTRIENT_DECIMALS,
  nutrientProgress,
  proteinDistribution,
  resolveActiveKcal,
  smoothWeightSeries,
  startOfWeek,
  type WeekDayState,
  weekdayNameLt,
  weeklyBudget,
  weightTrendPerWeek,
} from '@gramas/shared'
import {
  Button,
  Card,
  EmptyState,
  List,
  ListRow,
  NutrientGrid,
  NutrientMeter,
  SectionLabel,
  Segmented,
  Sparkline,
} from '@gramas/ui'
import { useLiveQuery } from 'dexie-react-hooks'
import { useState } from 'react'
import { db } from '../db/dexie.js'
import {
  bodyMeasurementsBetween,
  goalForDate,
  latestMeasurements,
  SLOT_LABEL,
  SLOT_ORDER,
} from '../db/repo.js'
import { WeekMotif } from '../glitime/motifs.js'
import { dailyLine } from '../sass/engine.js'
import { TrophyCabinet } from '../trophies/TrophyCabinet.js'
import { openWrapped } from '../wrapped/store.js'
import { currentWeekStart } from '../wrapped/Wrapped.js'
import { openBodySheet } from './sheets/bodySheetStore.js'
import { WeightSheet } from './sheets/WeightSheet.js'

type Range = '7' | '30'

/** How far back the Wrapped archive offers to go. */
const ARCHIVE_WEEKS = 12

/** Nutrients a food may legitimately not declare — tracked, never assumed zero. */
const OPTIONAL = ['sugars', 'satFat', 'fiber', 'salt'] as const
type OptionalKey = (typeof OPTIONAL)[number]

interface DayTotals extends Record<OptionalKey, number> {
  kcal: number
  protein: number
  carbs: number
  fat: number
  /** Protein per meal, which is what the daily figure alone cannot show. */
  proteinBySlot: Record<MealSlot, number>
  missing: Set<OptionalKey>
}

function emptyDay(): DayTotals {
  return {
    kcal: 0,
    protein: 0,
    carbs: 0,
    fat: 0,
    sugars: 0,
    satFat: 0,
    fiber: 0,
    salt: 0,
    proteinBySlot: { breakfast: 0, lunch: 0, dinner: 0, snack: 0 },
    missing: new Set(),
  }
}

const NUTRIENT_LABEL: Record<OptionalKey, string> = {
  fiber: 'Skaidulos',
  sugars: 'Cukrus',
  satFat: 'Sočiosios r.r.',
  salt: 'Druska',
}

/**
 * "−2,5 cm per 90 dienas" — the only thing a single measurement cannot say.
 *
 * Two readings are the minimum worth a sentence; one is a number the row is
 * already showing.
 */
/** The newest reading, decimal comma, or a dash — the body rows' right column. */
function lastValue(points: ReadonlyArray<{ date: string; value: number }>, digits = 1): string {
  const last = points.at(-1)
  return last == null ? '—' : last.value.toFixed(digits).replace('.', ',')
}

function changeSince(points: ReadonlyArray<{ date: string; value: number }>, unit: string): string {
  const first = points[0]
  const last = points.at(-1)
  if (!first || !last || points.length < 2) return 'Vienas matavimas'
  const delta = Math.round((last.value - first.value) * 10) / 10
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '±'
  return `${sign}${Math.abs(delta).toFixed(1).replace('.', ',')} ${unit} · ${points.length} matavimai`
}

export function Trends() {
  const [range, setRange] = useState<Range>('7')
  const [weightOpen, setWeightOpen] = useState(false)
  const days = Number(range)
  const today = localDateString(new Date(), deviceTimeZone())

  // A 90-day window regardless of the calorie range: the weight trend needs
  // weeks to say anything, and a 7-day view of it would be noise.
  const weights = useLiveQuery(
    () => bodyMeasurementsBetween(addDays(today, -90), today),
    [today],
    [],
  )
  const weightPoints = (weights ?? []).flatMap((m) =>
    m.weightKg == null ? [] : [{ date: m.measuredOn, weightKg: m.weightKg }],
  )
  const smoothed = smoothWeightSeries(weightPoints)
  const perWeek = weightTrendPerWeek(weightPoints)

  // Each field's own newest reading: a waist measured last week should still be
  // in the sheet after a fortnight of weight-only mornings.
  const body = useLiveQuery(() => latestMeasurements(today), [today], {
    weightKg: null,
    bodyFatPct: null,
    waistCm: null,
  })

  const thisWeek = currentWeekStart()

  /**
   * The Mondays worth offering, newest first — the current week plus however
   * many before it actually have entries. A dozen weeks back is the whole
   * archive for now; a diary long enough for that to feel short can grow a
   * "load more" when it gets there.
   */
  const archive = useLiveQuery(async () => {
    const from = addDays(thisWeek, -7 * ARCHIVE_WEEKS)
    const rows = await db.logEntries.where('logDate').between(from, thisWeek, true, false).toArray()
    const weeks = new Set(
      rows.filter((r) => r.deletedAt == null).map((r) => startOfWeek(r.logDate)),
    )
    return [...weeks].sort((a, b) => b.localeCompare(a))
  }, [thisWeek])

  /** Waist and body fat, when there is anything to say about them. */
  const waistPoints = (weights ?? []).flatMap((m) =>
    m.waistCm == null ? [] : [{ date: m.measuredOn, value: m.waistCm }],
  )
  const fatPoints = (weights ?? []).flatMap((m) =>
    m.bodyFatPct == null ? [] : [{ date: m.measuredOn, value: m.bodyFatPct }],
  )
  /** Only a scale fills these in; typing them by hand was never a thing. */
  const musclePoints = (weights ?? []).flatMap((m) =>
    m.muscleKg == null ? [] : [{ date: m.measuredOn, value: m.muscleKg }],
  )
  const waterPoints = (weights ?? []).flatMap((m) =>
    m.waterPct == null ? [] : [{ date: m.measuredOn, value: m.waterPct }],
  )
  // Four rows and a door. Protein, the water compartments, lean mass, bone,
  // the lean-mass burn and the rest live in the body sheet, with a sentence
  // each about what they are — a list of eleven figures here was a wall.
  const bodyRows = [waistPoints, fatPoints, musclePoints, waterPoints].filter((p) => p.length > 0)
  const hasComposition = fatPoints.length > 0
  const latestIsFromScale = (weights ?? []).at(-1)?.source === 'scale'

  /**
   * This calendar week as one budget instead of seven verdicts.
   *
   * The body settles up over weeks; only the app settles up at midnight. Eating
   * 300 under from Monday so Friday's restaurant fits is planning, and a
   * tracker that calls Friday a failure is teaching someone to stop logging
   * exactly the meals worth logging.
   *
   * Past days that were never written down are left out of both halves. Their
   * allowance would otherwise show up as a saving nobody made — the same reason
   * the adherence figure counts only logged days.
   */
  const week = useLiveQuery(async () => {
    const from = startOfWeek(today)
    const dates = Array.from({ length: 7 }, (_, i) => addDays(from, i))
    const entries = await db.logEntries.where('logDate').anyOf(dates).toArray()
    const burnRows = await db.wearableDaily.bulkGet(dates)
    const workouts = await db.workouts.where('logDate').anyOf(dates).toArray()

    const consumed = new Map<string, number>()
    for (const e of entries) {
      if (e.deletedAt != null) continue
      consumed.set(e.logDate, (consumed.get(e.logDate) ?? 0) + e.kcal)
    }

    let skipped = 0
    const days = (
      await Promise.all(
        dates.map(async (date, i) => {
          const state: WeekDayState = date < today ? 'past' : date === today ? 'today' : 'future'
          const kcal = consumed.get(date) ?? 0
          if (state === 'past' && kcal <= 0) {
            skipped++
            return null
          }
          const summary = burnRows[i]
          const burn = resolveActiveKcal(
            summary?.activeKcal == null
              ? []
              : [{ provider: summary.provider, activeKcal: summary.activeKcal }],
            workouts.filter((w) => w.logDate === date && w.deletedAt == null),
          ).activeKcal
          return {
            limitKcal: (await goalForDate(date)).kcalTarget + burn,
            consumedKcal: kcal,
            state,
          }
        }),
      )
    ).flatMap((day) => (day == null ? [] : [day]))

    return { from, to: dates[6] as string, skipped, budget: weeklyBudget(days) }
  }, [today])

  const data = useLiveQuery(async () => {
    const dates = Array.from({ length: days }, (_, i) => addDays(today, -(days - 1 - i)))
    const entries = await db.logEntries.where('logDate').anyOf(dates).toArray()
    const goal = await goalForDate(today)
    /**
     * The limit each day was actually kept to, not today's.
     *
     * Goals are effective-dated precisely because the phase changes, and this
     * screen is where a change shows. Grading the whole window against the
     * current target painted three on-plan weeks of a 2 400 maintenance red the
     * morning a 1 800 cut began.
     */
    const targetOn = new Map(
      await Promise.all(
        dates.map(async (date) => [date, (await goalForDate(date)).kcalTarget] as const),
      ),
    )
    // Everything the day's burn added to its limit, the same way Today's ring
    // budgets it: a training day eaten to its adjusted limit is not over.
    const burnRows = await db.wearableDaily.bulkGet(dates)
    const workouts = await db.workouts.where('logDate').anyOf(dates).toArray()
    const burnOn = new Map(
      dates.map((date, i) => {
        const summary = burnRows[i]
        return [
          date,
          resolveActiveKcal(
            summary?.activeKcal == null
              ? []
              : [{ provider: summary.provider, activeKcal: summary.activeKcal }],
            workouts.filter((w) => w.logDate === date && w.deletedAt == null),
          ).activeKcal,
        ] as const
      }),
    )

    const byDate = new Map<string, DayTotals>()
    for (const date of dates) byDate.set(date, emptyDay())
    for (const e of entries) {
      if (e.deletedAt != null) continue
      const bucket = byDate.get(e.logDate)
      if (!bucket) continue
      bucket.kcal += e.kcal
      bucket.protein += e.protein
      bucket.proteinBySlot[e.slot] += e.protein
      bucket.carbs += e.carbs
      bucket.fat += e.fat
      // A day is only comparable against a limit if every food in it declared
      // the nutrient; one undeclared item makes the day's total an undercount,
      // so it is tracked as unknown rather than as a smaller number.
      for (const key of OPTIONAL) {
        const value = e[key]
        if (value == null) bucket.missing.add(key)
        else bucket[key] += value
      }
    }
    return { dates, byDate, goal, targetOn, burnOn }
  }, [days, today])

  if (!data) return null

  const logged = data.dates.filter((d) => (data.byDate.get(d)?.kcal ?? 0) > 0)
  const series = data.dates.map((d) => data.byDate.get(d)?.kcal ?? 0)
  const average = logged.length
    ? logged.reduce((sum, d) => sum + (data.byDate.get(d)?.kcal ?? 0), 0) / logged.length
    : 0
  const avgProtein = logged.length
    ? logged.reduce((sum, d) => sum + (data.byDate.get(d)?.protein ?? 0), 0) / logged.length
    : 0

  // Averaged over the days that actually declared the nutrient. Including a day
  // where one item had no salt figure would drag the mean down and make a diet
  // look better than it was.
  const averages = Object.fromEntries(
    OPTIONAL.map((key) => {
      const complete = logged.filter((d) => !data.byDate.get(d)?.missing.has(key))
      if (complete.length === 0) return [key, null]
      const sum = complete.reduce((total, d) => total + (data.byDate.get(d)?.[key] ?? 0), 0)
      return [key, sum / complete.length]
    }),
  ) as Record<OptionalKey, number | null>

  const incomplete = logged.filter((d) => (data.byDate.get(d)?.missing.size ?? 0) > 0).length

  // Each day against the limit that was in force on it, burn included — the
  // same test the rows below colour themselves by.
  const outcomes = data.dates.map((date) => {
    const kcal = data.byDate.get(date)?.kcal ?? 0
    const limit = (data.targetOn.get(date) ?? data.goal.kcalTarget) + (data.burnOn.get(date) ?? 0)
    return { logged: kcal > 0, withinTarget: kcal <= limit }
  })
  const streak = loggingStreak(outcomes)
  const adherence = adherenceRate(outcomes)

  /**
   * Days that put most of their protein into one sitting.
   *
   * The daily total is the number every tracker shows, and it is the number
   * that hides this: 160 g is a good day spread over four meals and a mediocre
   * one when 120 g of it was dinner, because synthesis responds to a dose and
   * saturates above it. The entries were already filed into meals, so nothing
   * extra has to be recorded to say so.
   */
  const skewed = logged.flatMap((date) => {
    const day = data.byDate.get(date)
    if (!day) return []
    const split = proteinDistribution(
      SLOT_ORDER.map((slot) => ({ slot, proteinG: day.proteinBySlot[slot] })),
    )
    return split.skewed && split.topSlot != null ? [split.topSlot] : []
  })
  const skewedSlot = skewed
    .slice(1)
    .reduce(
      (top, slot) =>
        skewed.filter((s) => s === slot).length > skewed.filter((s) => s === top).length
          ? slot
          : top,
      skewed[0],
    )

  const fallback = defaultSecondaryTargets(data.goal.kcalTarget)
  const targets: Record<OptionalKey, number> = {
    fiber: data.goal.fiberG ?? fallback.fiber,
    sugars: data.goal.sugarsG ?? fallback.sugars,
    satFat: data.goal.satFatG ?? fallback.satFat,
    salt: data.goal.saltG ?? fallback.salt,
  }

  // The voice's remarks for this screen, all pinned per date and all null in
  // neutral mode, where the existing sentences below stand unchanged.
  const weekLine =
    week == null
      ? null
      : week.budget.isOver
        ? dailyLine('weekly_budget_over', today, { overByKcal: -week.budget.remainingKcal })
        : week.budget.perDayKcal == null
          ? null
          : dailyLine('weekly_budget_left', today, {
              perDayKcal: week.budget.perDayKcal,
              daysLeft: week.budget.daysLeft,
            })
  // Streak commentary only on the week marks — a line that fired daily would
  // wear out both the pool and the compliment.
  const streakLine =
    streak >= 7 && streak % 7 === 0 ? dailyLine('streak', today, { streakDays: streak }) : null
  const skewLine = skewed.length > 0 && skewedSlot != null ? dailyLine('protein_skew', today) : null
  const weightLine =
    perWeek == null ? null : dailyLine('weight_trend', today, { weightTrendKgWk: perWeek })
  const emptyTitle = logged.length === 0 ? dailyLine('empty_trends', today) : null

  return (
    <div className="h-full overflow-y-auto overscroll-contain">
      <header className="flex items-center justify-between border-b border-line px-4 py-2">
        <h1 className="font-display text-lg font-semibold text-ink">Tendencijos</h1>
        <Segmented
          label="Laikotarpis"
          value={range}
          onValueChange={setRange}
          options={[
            { value: '7', label: '7 d.' },
            { value: '30', label: '30 d.' },
          ]}
        />
      </header>

      <SectionLabel>Svoris</SectionLabel>
      <section className="px-4">
        <Card data-tour="trends-weight" className="p-4">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="flex items-center gap-1.5 text-xs text-mute">
                Paskutinis matavimas
                {latestIsFromScale && (
                  <span className="rounded-full bg-line px-1.5 py-0.5 text-[10px] leading-none text-dim">
                    svarstyklės
                  </span>
                )}
              </div>
              <div className="tnum font-display text-2xl font-semibold text-ink">
                {smoothed.at(-1) == null
                  ? '—'
                  : (weightPoints.at(-1)?.weightKg ?? 0).toFixed(1).replace('.', ',')}{' '}
                <span className="text-base font-normal text-mute">kg</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-mute">Tendencija</div>
              <div className="tnum text-base text-dim">
                {perWeek == null
                  ? '—'
                  : `${perWeek > 0 ? '+' : ''}${perWeek.toFixed(2).replace('.', ',')} kg/sav.`}
              </div>
            </div>
          </div>
          {smoothed.length > 1 && (
            <div className="mt-3">
              <Sparkline points={smoothed.map((p) => p.weightKg)} height={48} />
            </div>
          )}
          <p className="mt-2 text-xs text-mute">
            {perWeek == null
              ? 'Reikia bent dviejų savaičių matavimų, kad tendencija būtų patikima.'
              : (weightLine ??
                `Atitinka apie ${impliedEnergyBalance(perWeek)} kcal per dieną nuo pusiausvyros.`)}
          </p>
          <div className="mt-3 flex gap-2">
            <Button block onClick={() => setWeightOpen(true)}>
              Įrašyti svorį
            </Button>
            {hasComposition && (
              <Button block onClick={openBodySheet}>
                Kūno sudėtis
              </Button>
            )}
          </div>
        </Card>
      </section>

      {logged.length === 0 ? (
        <EmptyState
          motif={<WeekMotif />}
          title={emptyTitle ?? 'Dar nėra duomenų'}
          hint="Įrašykite bent vieną dieną, kad matytumėte tendencijas."
        />
      ) : (
        <>
          <section className="px-4 pt-4">
            <Card className="p-4">
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-xs text-mute">Vidutiniškai per dieną</div>
                  <div className="tnum font-display text-2xl font-semibold text-ink">
                    {Math.round(average)}{' '}
                    <span className="text-base font-normal text-mute">kcal</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-mute">Tikslas</div>
                  <div className="tnum text-base text-dim">{data.goal.kcalTarget} kcal</div>
                </div>
              </div>
              <div className="mt-3">
                <Sparkline points={series} target={data.goal.kcalTarget} height={56} />
              </div>
              <div className="mt-2 flex justify-between text-xs text-mute">
                <span>{data.dates[0]}</span>
                <span>{data.dates.at(-1)}</span>
              </div>
            </Card>
          </section>

          <section className="px-4 pt-3">
            <Card className="flex divide-x divide-[var(--g-line)]">
              {[
                ['Baltymai', avgProtein, data.goal.proteinG],
                ['Užfiksuota dienų', logged.length, days],
              ].map(([label, value, target]) => (
                <div key={label as string} className="flex-1 px-3 py-3">
                  <div className="text-xs text-mute">{label}</div>
                  <div className="tnum text-lg text-ink">
                    {Math.round(value as number)}
                    {target != null && (
                      <span className="text-sm text-mute">/{Math.round(target as number)}</span>
                    )}
                  </div>
                </div>
              ))}
            </Card>
          </section>

          {/* The week as a budget. A day is a modelling convenience; the body
              settles up over weeks, and so should the screen that judges it. */}
          {week != null && week.budget.daysLeft > 0 && (
            <section className="px-4 pt-3">
              <Card className="p-4">
                <div className="flex items-baseline justify-between">
                  <div>
                    <div className="text-xs text-mute">Liko šią savaitę</div>
                    <div className="tnum font-display text-2xl font-semibold text-ink">
                      <span className={week.budget.isOver ? 'text-danger' : undefined}>
                        {week.budget.isOver ? '−' : ''}
                        {Math.abs(week.budget.remainingKcal)}
                      </span>{' '}
                      <span className="text-base font-normal text-mute">kcal</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-mute">Po</div>
                    <div className="tnum text-base text-dim">
                      {/* Nothing useful to say once the week is spent: "valgykite
                          −667 kcal" is arithmetic, not advice. */}
                      {week.budget.isOver || week.budget.perDayKcal == null
                        ? '—'
                        : `${week.budget.perDayKcal} kcal × ${week.budget.daysLeft} d.`}
                    </div>
                  </div>
                </div>
                <p className="mt-2 text-xs text-mute">
                  {weekLine ??
                    (week.budget.isOver
                      ? 'Savaitės biudžetas jau išnaudotas — likusios dienos eina į minusą.'
                      : 'Sunkesnė vakarienė gali būti pasiskolinta iš savaitės, o ne laikoma nepavykusia diena.')}
                  {week.skipped > 0 && ` Neįrašytos ${week.skipped} d. neįskaičiuotos.`}
                </p>
              </Card>
            </section>
          )}

          {/* Two figures about the habit rather than about the food. A diary is
              only as good as the days that make it into it, and neither of these
              is worth anything without the other: a perfect week of three logged
              days is three logged days. */}
          <section className="px-4 pt-2">
            <Card className="flex divide-x divide-[var(--g-line)]">
              <div className="flex-1 px-3 py-3">
                <div className="text-xs text-mute">Iš eilės</div>
                <div className="tnum text-lg text-ink">
                  {streak}
                  <span className="text-sm text-mute"> d.</span>
                </div>
              </div>
              <div className="flex-1 px-3 py-3">
                <div className="text-xs text-mute">Neviršyta</div>
                <div className="tnum text-lg text-ink">
                  {adherence == null ? '—' : `${Math.round(adherence * 100)}%`}
                  {adherence != null && (
                    <span className="text-sm text-mute"> iš {logged.length} d.</span>
                  )}
                </div>
              </div>
            </Card>
            {streakLine != null && <p className="pt-1.5 text-xs text-mute">{streakLine}</p>}
            {skewed.length > 0 && skewedSlot != null && (
              <p className="pt-1.5 text-xs text-mute">
                {skewLine ??
                  `${skewed.length} d. iš ${logged.length} didžioji baltymų dalis suvalgyta vienu ` +
                    `kartu, dažniausiai tai ${SLOT_LABEL[skewedSlot].toLowerCase()}. Raumenys ` +
                    'baltymus geriau pasisavina keliomis porcijomis per dieną.'}
              </p>
            )}
          </section>

          <SectionLabel>Vidurkis prieš normą</SectionLabel>
          <div className="px-4">
            <NutrientGrid>
              {OPTIONAL.map((key) => (
                <NutrientMeter
                  key={key}
                  label={NUTRIENT_LABEL[key]}
                  progress={nutrientProgress(key, averages[key], targets[key])}
                  decimals={NUTRIENT_DECIMALS[key]}
                />
              ))}
            </NutrientGrid>
            {incomplete > 0 && (
              <p className="pb-3 text-xs text-mute">
                {incomplete} d. praleista — tą dieną ne visi produktai nurodė šias medžiagas.
              </p>
            )}
          </div>

          <SectionLabel>Dienos</SectionLabel>
          <List className="border-y border-line bg-surface">
            {[...data.dates].reverse().map((date, i, arr) => {
              const day = data.byDate.get(date)!
              const limit =
                (data.targetOn.get(date) ?? data.goal.kcalTarget) + (data.burnOn.get(date) ?? 0)
              const over = day.kcal > limit
              return (
                <ListRow
                  key={date}
                  last={i === arr.length - 1}
                  title={weekdayNameLt(date)}
                  subtitle={date}
                  value={
                    <span className={over ? 'text-danger' : undefined}>
                      {day.kcal > 0 ? Math.round(day.kcal) : '—'}
                    </span>
                  }
                  meta={day.kcal > 0 ? `B ${Math.round(day.protein)} g` : undefined}
                />
              )
            })}
          </List>
        </>
      )}
      {bodyRows.length > 0 && (
        <>
          <SectionLabel>Kūno matmenys</SectionLabel>
          <List className="border-y border-line bg-surface">
            {waistPoints.length > 0 && (
              <ListRow
                last={bodyRows.at(-1) === waistPoints && !hasComposition}
                title="Juosmuo"
                subtitle={changeSince(waistPoints, 'cm')}
                value={lastValue(waistPoints)}
                meta="cm"
              />
            )}
            {fatPoints.length > 0 && (
              <ListRow
                last={bodyRows.at(-1) === fatPoints && !hasComposition}
                title="Kūno riebalai"
                subtitle={changeSince(fatPoints, '%')}
                value={lastValue(fatPoints)}
                meta="%"
              />
            )}
            {musclePoints.length > 0 && (
              <ListRow
                last={bodyRows.at(-1) === musclePoints && !hasComposition}
                title="Raumenys"
                subtitle={changeSince(musclePoints, 'kg')}
                value={lastValue(musclePoints)}
                meta="kg"
              />
            )}
            {waterPoints.length > 0 && (
              <ListRow
                last={bodyRows.at(-1) === waterPoints && !hasComposition}
                title="Vanduo"
                subtitle={changeSince(waterPoints, '%')}
                value={lastValue(waterPoints)}
                meta="%"
              />
            )}
            {hasComposition && (
              <ListRow
                last
                title="Visa kūno sudėtis"
                subtitle="baltymai, vanduo ląstelėse, liesoji masė, apykaita, kūno tipas"
                wrapSubtitle
                value="›"
                onActivate={openBodySheet}
              />
            )}
          </List>
        </>
      )}
      <SectionLabel>Savaitė</SectionLabel>
      <div className="px-4">
        <Button block onClick={() => openWrapped(thisWeek)}>
          Šios savaitės Wrapped
        </Button>
      </div>
      {(archive ?? []).length > 0 && (
        <>
          <SectionLabel>Praėjusios savaitės</SectionLabel>
          <List className="border-y border-line bg-surface">
            {(archive ?? []).map((weekStart, i) => (
              <ListRow
                key={weekStart}
                last={i === (archive ?? []).length - 1}
                title={`${weekStart} – ${addDays(weekStart, 6)}`}
                subtitle="Savaitės suvestinė"
                onActivate={() => openWrapped(weekStart)}
              />
            ))}
          </List>
        </>
      )}

      <TrophyCabinet />

      <div className="h-6" />

      <WeightSheet open={weightOpen} date={today} current={body} onOpenChange={setWeightOpen} />
    </div>
  )
}
